import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LocalDate } from "@/components/ui/local-date";
import { ApiError, api } from "@/lib/api";
import { useApiMutation } from "@/lib/use-api-mutation";
import { Link, createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { useState } from "react";

/**
 * `/accounting/missing` — bank movements still waiting for a document,
 * grouped by who was paid, so it reads as a list of invoices to ask for.
 */

type Movement = {
  id: string;
  bookedOn: string;
  amount: string;
  currency: string;
  variableSymbol: string | null;
  card: { amount: string; currency: string } | null;
  message: string | null;
};

type Group = {
  party: string;
  count: number;
  total: string;
  from: string;
  to: string;
  movements: Movement[];
};

type Missing = { expenses: Group[]; income: Group[] };

export const Route = createFileRoute("/_app/accounting/missing")({
  loader: async () => {
    try {
      return await api.get<Missing>("/api/accounting/bank/missing-documents");
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) throw redirect({ to: "/my" });
      throw err;
    }
  },
  component: MissingPage,
});

function money(amount: string, currency: string): string {
  return `${Number(amount).toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}

function MissingPage() {
  const { expenses, income } = Route.useLoaderData();
  return (
    <div className="space-y-6 p-6">
      <div className="space-y-1">
        <h1 className="font-semibold text-2xl">Missing documents</h1>
        <p className="text-muted-foreground text-sm">
          Bank movements nothing is paired with yet. Send the documents to the{" "}
          <Link to="/accounting/inbox" className="underline">
            Inbox
          </Link>
          ; a movement that never has a document (a loan, a transfer between own accounts) is put
          aside with a reason.
        </p>
      </div>
      <Section
        title="Payments without an expense document"
        description="Invoices or receipts to get from these suppliers."
        groups={expenses}
      />
      <Section
        title="Income without an invoice"
        description="Money that came in and pays no invoice in the books."
        groups={income}
      />
    </div>
  );
}

function Section({
  title,
  description,
  groups,
}: {
  title: string;
  description: string;
  groups: Group[];
}) {
  const count = groups.reduce((sum, g) => sum + g.count, 0);
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {title} <span className="text-muted-foreground text-base">({count})</span>
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-1 text-sm">
        {groups.length === 0 ? (
          <p className="text-muted-foreground">Nothing is missing.</p>
        ) : (
          groups.map((group) => <GroupRow key={group.party} group={group} />)
        )}
      </CardContent>
    </Card>
  );
}

function GroupRow({ group }: { group: Group }) {
  const [open, setOpen] = useState(false);
  const currency = group.movements[0]?.currency ?? "CZK";
  return (
    <div className="border-b py-1 last:border-b-0">
      <button
        type="button"
        className="flex w-full flex-wrap items-center gap-3 text-left hover:underline"
        onClick={() => setOpen(!open)}
      >
        <span className="min-w-48 font-medium">{group.party}</span>
        <Badge variant="secondary">{group.count}×</Badge>
        <span className="tabular-nums">{money(group.total, currency)}</span>
        <span className="text-muted-foreground text-xs">
          <LocalDate value={group.from} />
          {group.count > 1 && (
            <>
              {" – "}
              <LocalDate value={group.to} />
            </>
          )}
        </span>
      </button>
      {open && (
        <ul className="mt-1 ml-4 space-y-1">
          {group.movements.map((movement) => (
            <MovementRow key={movement.id} movement={movement} />
          ))}
        </ul>
      )}
    </div>
  );
}

function MovementRow({ movement }: { movement: Movement }) {
  const router = useRouter();
  const putAside = useApiMutation(
    (reason: string) =>
      api.post(`/api/accounting/bank/transactions/${movement.id}/ignore`, { reason }),
    { onSuccess: () => router.invalidate(), fallbackMessage: "Couldn't put it aside." },
  );
  return (
    <li className="flex flex-wrap items-center gap-3 text-xs">
      <LocalDate value={movement.bookedOn} />
      <span className="tabular-nums">{money(movement.amount, movement.currency)}</span>
      {movement.card && (
        <span className="text-muted-foreground">
          card {movement.card.amount} {movement.card.currency}
        </span>
      )}
      {movement.variableSymbol && (
        <span className="text-muted-foreground">VS {movement.variableSymbol}</span>
      )}
      {movement.message && <span className="text-muted-foreground">{movement.message}</span>}
      <Button
        size="sm"
        variant="ghost"
        className="h-6 px-2 text-xs"
        disabled={putAside.pending}
        onClick={() => {
          const reason = window.prompt("Why does this movement need no document?");
          if (reason && reason.trim().length >= 2) putAside.mutate(reason.trim());
        }}
      >
        No document needed
      </Button>
      {putAside.error && <span className="text-destructive">{putAside.error}</span>}
    </li>
  );
}
